import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import I from "../Icons";
import { cancelGenerationJob, generationStatusLabel, regenerateGenerationOutput, retryGenerationOutput } from "../../lib/generationOutputs";
import { modeLabel } from "../../lib/generationModes";
import { common, pick } from "../../lib/i18n";

const copy = {
  pl: { previous: "Poprzedni", next: "Następny", resultsTitle: "Wyniki generacji", completed: "ukończonych", finished: "zakończone", stop: "Zatrzymaj", downloadZip: "Pobierz ZIP", fullView: "Pełny widok", notif: (d: number, t: number) => `Generacja ukończona — ${d} z ${t} grafik gotowych.` },
  en: { previous: "Previous", next: "Next", resultsTitle: "Generation results", completed: "completed", finished: "finished", stop: "Stop", downloadZip: "Download ZIP", fullView: "Full view", notif: (d: number, t: number) => `Generation finished — ${d} of ${t} images ready.` },
};
import useGenerationSocket from "../../hooks/useGenerationSocket";
import type { ActiveGenerationJob, GenerationOutputUpdate } from "../../types";

type Props = {
  job: ActiveGenerationJob;
  productId: number;
};

type NavDir = "open" | "prev" | "next";

function GalleryModal({
  outputs,
  initialOutputId,
  productId,
  jobId,
  onClose,
  onRetry,
  onRegenerate,
  retrying,
}: {
  outputs: GenerationOutputUpdate[];
  initialOutputId: number;
  productId: number;
  jobId: number;
  onClose: () => void;
  onRetry: (output: GenerationOutputUpdate) => void;
  onRegenerate: (output: GenerationOutputUpdate) => void;
  retrying: number | null;
}) {
  const t = pick(copy);
  const c = common();
  const viewable = outputs.filter((o) => o.image_url || o.error_message);
  const [currentId, setCurrentId] = useState(initialOutputId);
  const [dir, setDir] = useState<NavDir>("open");
  const current = viewable.find((o) => o.output_id === currentId) ?? viewable[0];
  const idx = viewable.findIndex((o) => o.output_id === current?.output_id);

  const navigate = useCallback((delta: -1 | 1) => {
    const next = viewable[idx + delta];
    if (!next) return;
    setDir(delta === 1 ? "next" : "prev");
    setCurrentId(next.output_id);
  }, [idx, viewable]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") navigate(1);
      if (e.key === "ArrowLeft")  navigate(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, onClose]);

  if (!current) return null;

  return createPortal(
    <div
      className="v3-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="v3-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="v3-modal-topbar">
          <div>
            <div className="v3-modal-title">{modeLabel(current.mode)}</div>
            <div className="v3-modal-counter">{idx + 1} / {viewable.length}</div>
          </div>
          <div className="v3-modal-actions">
            {current.status === "failed" && (
              <button
                className="btn btn-ghost btn-sm"
                style={{ height: 32, color: "#fff", borderColor: "oklch(1 0 0 / .25)" }}
                disabled={retrying === current.output_id}
                onClick={() => onRetry(current)}
              >
                {c.actions.retry}
              </button>
            )}
            {current.status === "done" && current.image_url && (
              <button
                className="v3-modal-action-btn"
                title={c.actions.generateAgain}
                aria-label={c.actions.generateAgain}
                disabled={retrying === current.output_id}
                onClick={() => onRegenerate(current)}
              >
                {I.refresh}
              </button>
            )}
            {current.image_url && (
              <a
                href={current.image_url}
                download
                className="v3-modal-action-btn"
                aria-label={c.actions.download}
              >
                {I.download}
              </a>
            )}
            <button className="v3-modal-close-floating" onClick={onClose} aria-label={c.actions.close}>
              {I.x}
            </button>
          </div>
        </div>
        <div className="v3-modal-img-wrap">
          <button className="v3-modal-nav prev" disabled={idx === 0} onClick={() => navigate(-1)} aria-label={t.previous}>‹</button>

          {current.image_url ? (
            <img
              key={current.output_id}
              src={current.image_url}
              className={`v3-modal-img dir-${dir}`}
              alt={modeLabel(current.mode)}
            />
          ) : (
            <div style={{ color: "#c0392b", fontSize: 14, padding: 32, background: "oklch(1 0 0 / .06)", borderRadius: 8 }}>
              ⚠ {current.error_message}
            </div>
          )}

          <button className="v3-modal-nav next" disabled={idx === viewable.length - 1} onClick={() => navigate(1)} aria-label={t.next}>›</button>
        </div>

      </div>
    </div>,
    document.body
  );
}

export default function InlineResultsSection({ job, productId }: Props) {
  const t = pick(copy);
  const c = common();
  const [overrides, setOverrides] = useState<Record<number, Partial<GenerationOutputUpdate>>>({});
  const [retrying, setRetrying] = useState<number | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [modalOutputId, setModalOutputId] = useState<number | null>(null);
  const [hoveredOutputId, setHoveredOutputId] = useState<number | null>(null);

  const outputs = useGenerationSocket(job.id, job.outputs.map((output) => ({ ...output, ...overrides[output.output_id] })));

  const done = outputs.filter((o) => o.status === "done").length;
  const total = outputs.length;
  const allFinished = outputs.every((o) => o.status === "done" || o.status === "failed");

  const prevAllFinished = useRef(allFinished);
  useEffect(() => {
    if (allFinished && !prevAllFinished.current && document.hidden) {
      const fire = () => new Notification("AvantoAI", {
        body: t.notif(done, total),
        icon: "/favicon.ico",
      });
      if (Notification.permission === "granted") {
        fire();
      } else if (Notification.permission === "default") {
        Notification.requestPermission().then((p) => { if (p === "granted") fire(); });
      }
    }
    prevAllFinished.current = allFinished;
  }, [allFinished, done, total]);

  const cancelJob = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await cancelGenerationJob(productId, job.id);
      setOverrides((prev) => {
        const next = { ...prev };
        outputs.forEach((o) => {
          if (o.status === "queued") next[o.output_id] = { status: "cancelled" };
        });
        return next;
      });
    } finally {
      setCancelling(false);
    }
  };

  const retryOutput = async (output: GenerationOutputUpdate) => {
    if (retrying) return;
    setRetrying(output.output_id);
    setRetryError(null);
    setOverrides((prev) => ({
      ...prev,
      [output.output_id]: { status: "queued", image_url: undefined, error_message: undefined, prompt_used: undefined },
    }));
    try {
      await retryGenerationOutput(productId, job.id, output.output_id);
      setModalOutputId(null);
    } catch {
      setRetryError(c.errors.retryGenerationFailed);
      setOverrides((prev) => ({
        ...prev,
        [output.output_id]: { status: "failed", error_message: output.error_message },
      }));
    } finally {
      setRetrying(null);
    }
  };

  const regenerateOutput = async (output: GenerationOutputUpdate) => {
    if (retrying) return;
    setRetrying(output.output_id);
    setRetryError(null);
    setOverrides((prev) => ({
      ...prev,
      [output.output_id]: {
        status: "queued",
        image_url: undefined,
        error_message: undefined,
        prompt_used: output.prompt_used,
      },
    }));
    try {
      await regenerateGenerationOutput(productId, job.id, output.output_id);
      setModalOutputId(null);
    } catch {
      setRetryError(c.errors.regenerateGenerationFailed);
      setOverrides((prev) => ({
        ...prev,
        [output.output_id]: {
          status: output.status,
          image_url: output.image_url,
          prompt_used: output.prompt_used,
          error_message: output.error_message,
        },
      }));
    } finally {
      setRetrying(null);
    }
  };

  return (
    <div className="v3-sec v3-slide-in">
      <div style={{ padding: "20px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{t.resultsTitle}</div>
            <div className="mono" style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
              {done}/{total} {t.completed}
              {allFinished && <span style={{ color: "var(--accent)", marginLeft: 8 }}>✓ {t.finished}</span>}
            </div>
          </div>
          {retryError && <div className="v3-alert-error">{retryError}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            {!allFinished && (
              <button
                className="btn btn-ghost btn-sm"
                style={{ height: 34, color: "var(--danger, #c0392b)", borderColor: "currentColor" }}
                onClick={cancelJob}
                disabled={cancelling}
              >
                {cancelling ? <span className="v3-spin" style={{ marginRight: 4 }} /> : null}
                {t.stop}
              </button>
            )}
            {allFinished && done > 0 && (
              <a href={`/products/${productId}/generation_jobs/${job.id}/download`} className="btn btn-primary btn-sm" style={{ height: 34 }}>
                {I.download} {t.downloadZip}
              </a>
            )}
            <a href={`/products/${productId}/generation_jobs/${job.id}`} className="btn btn-ghost btn-sm" style={{ height: 34 }}>
              {t.fullView}
            </a>
          </div>
        </div>

        <div style={{ height: 3, background: "var(--border)", borderRadius: 2, marginBottom: 20, overflow: "hidden" }}>
          <div style={{ height: "100%", background: "var(--accent)", borderRadius: 2, width: `${(done / total) * 100}%`, transition: "width .4s ease" }} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {outputs.map((output) => {
            const canOpen = !!(output.image_url || output.error_message);
            return (
              <div
                key={output.output_id}
                onClick={() => canOpen && setModalOutputId(output.output_id)}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  overflow: "hidden",
                  cursor: canOpen ? "pointer" : "default",
                  background: "var(--panel)",
                  transition: "border-color .15s, box-shadow .15s",
                }}
                onMouseEnter={(e) => { setHoveredOutputId(output.output_id); if (canOpen) { (e.currentTarget as HTMLDivElement).style.borderColor = "var(--accent)"; (e.currentTarget as HTMLDivElement).style.boxShadow = "0 0 0 1px var(--accent)"; } }}
                onMouseLeave={(e) => { setHoveredOutputId(null); (e.currentTarget as HTMLDivElement).style.borderColor = "var(--border)"; (e.currentTarget as HTMLDivElement).style.boxShadow = "none"; }}
              >
                <div style={{ aspectRatio: "1/1", position: "relative", background: "var(--bg-2)" }}>
                  {output.image_url ? (
                    <img key={output.image_url} src={output.image_url} className="v3-img-tile" />
                  ) : (
                    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
                      {output.status === "running"   && (
                        <>
                          <span className="v3-spin-lg" />
                          {output.step && (
                            <span style={{ fontSize: 10, color: "var(--muted)", maxWidth: "80%", textAlign: "center", lineHeight: 1.3 }}>
                              {output.step}
                            </span>
                          )}
                        </>
                      )}
                      {output.status === "queued"    && <div className="v3-skel" style={{ width: 48, height: 48, borderRadius: 4 }} />}
                      {output.status === "failed"    && <span style={{ fontSize: 20 }}>⚠</span>}
                      {output.status === "cancelled" && <span style={{ fontSize: 20, color: "var(--muted)" }}>—</span>}
                    </div>
                  )}
                  {output.status === "done" && output.image_url && hoveredOutputId === output.output_id && (
                    <button
                      type="button"
                      className="btn btn-icon btn-sm"
                      title={c.actions.generateAgain}
                      aria-label={c.actions.generateAgain}
                      disabled={retrying === output.output_id}
                      onClick={(e) => {
                        e.stopPropagation();
                        regenerateOutput(output);
                      }}
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        width: 28,
                        height: 28,
                        background: "oklch(1 0 0 / .92)",
                        borderColor: "oklch(0 0 0 / .12)",
                        boxShadow: "0 8px 20px oklch(0 0 0 / .18)",
                      }}
                    >
                      {I.refresh}
                    </button>
                  )}
                </div>
                <div style={{ padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 500 }}>{modeLabel(output.mode)}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {output.status === "failed" && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ height: 24, padding: "0 8px", fontSize: 11 }}
                        onClick={(e) => { e.stopPropagation(); retryOutput(output); }}
                        disabled={retrying === output.output_id}
                      >
                        {c.actions.retry}
                      </button>
                    )}
                    <span style={{ fontSize: 11, color: output.status === "done" ? "var(--accent)" : output.status === "failed" ? "#c0392b" : "var(--muted)" }} title={output.status === "cancelled" ? c.status.cancelled : undefined}>
                      {output.status === "running" && <span className="v3-spin" style={{ marginRight: 4 }} />}
                      {generationStatusLabel(output.status)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modalOutputId !== null && (
        <GalleryModal
          outputs={outputs}
          initialOutputId={modalOutputId}
          productId={productId}
          jobId={job.id}
          onClose={() => setModalOutputId(null)}
          onRetry={retryOutput}
          onRegenerate={regenerateOutput}
          retrying={retrying}
        />
      )}
    </div>
  );
}
